import { useTranslation } from "react-i18next";
import { PrimaryCard } from "../common/cards";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, HStack, Icon, IconButton, RangeSlider, RangeSliderThumb, RangeSliderTrack, TagLabel, Text, VStack } from "@chakra-ui/react";
import { MdPause, MdPlayArrow } from "react-icons/md";
import { Line } from 'react-chartjs-2';
import { CategoryScale, Chart, Filler, LinearScale, LineElement, PointElement, Tooltip } from "chart.js";
import { useAssistance } from "../../hooks";
import annotationPlugin from 'chartjs-plugin-annotation';
import "./PlayerCard.css"
import { PrimaryTag } from "../common/tags";
import { Canvas } from "@react-three/fiber";
import SceneViewer from "./SceneViewer";
import { gsap } from "gsap";

Chart.register(CategoryScale, PointElement, LinearScale, LineElement, Filler, Tooltip, annotationPlugin);

/**
 * Renders the player card
 * 
 * @returns The player card
 */
const PlayerCard = () => {
    const { t } = useTranslation();
    const [playing, setPlaying] = useState(true);
    const [cursor, setCursor] = useState(0);
    const timeline = useRef(gsap.timeline({ paused: true, repeat: 0, defaults: { duration: 0.05, ease: 'power1.in' } })).current;
    const { crashData, crashImpactTime } = useAssistance();
    const sceneViewerRef = useRef(null);

    const chart = useRef();

    const crashLabels = useMemo(() => crashData?.vehicle0?.time?.map((time) => time?.toFixed(2)), [crashData?.vehicle0?.time]);
    const crashSpeed = useMemo(() => crashData?.vehicle0?.speed_magnitude?.map((v) => v * 3.6), [crashData?.vehicle0?.speed_magnitude]);
    const indexByLabels = useMemo(() => crashLabels?.reduce((prev, value, index) => ({ ...prev, [value]: index }), {}), [crashLabels]);
    const data = useMemo(() => ({
        labels: crashLabels,
        datasets: [
            {
                data: crashSpeed,
                borderColor: "white",
                borderWidth: 1,
                pointRadius: 0,
                backgroundColor: (context) => {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;
                    if (!chartArea) {
                        return;
                    }
                    const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
                    gradient.addColorStop(0, "#FFFFFF00");
                    gradient.addColorStop(0.6, "#FFFFFF54");
                    gradient.addColorStop(1, "#FFFFFF");
                    return gradient;
                },
                fill: "start"
            }
        ]
    }), [crashLabels, crashSpeed]);

    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: "nearest",
            intersect: false,
            axis: "x",
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    display: false,
                },
            },
            y: {
                grid: {
                    color: "#FFFFFF33",
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: "white",
                }
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    title: (tooltipItems) => {
                        return tooltipItems?.[0]?.label + "s";
                    },
                    label: (tooltipItems) => {
                        return parseFloat(tooltipItems?.[0]?.formattedValue || 0)?.toFixed(0) + " km/h";
                    }

                }
            },
            annotation: {
                annotations: [
                    ...(crashImpactTime || []).map(impact => ({
                        type: 'line',
                        mode: 'vertical',
                        scaleID: 'x',
                        value: impact?.toFixed(2),
                        borderColor: '#FD2D2280',
                        borderWidth: 4,
                        label: {
                            color: "#FD2D2280",
                            position: "top",
                            display: true,
                            backgroundColor: "transparent",
                            xAdjust: -10,
                            rotation: 270,
                        }
                    }))
                ]
            },
        },
    }), [crashImpactTime]);

    const currentSpeed = useMemo(
        () => crashSpeed?.[(crashSpeed?.length * parseFloat(cursor) / 100)?.toFixed(0)]?.toFixed(0),
        [crashSpeed, cursor]
    );

    const maxSpeed = useMemo(
        () => crashSpeed?.[indexByLabels[crashImpactTime?.[0]?.toFixed(2)]]?.toFixed(0),
        [crashImpactTime, crashSpeed, indexByLabels]);

    /**
     * Toggles the play/pause state of the timeline
     */
    const togglePlayPause = useCallback(() => {
        if (timeline.progress() === 1) {
            timeline.restart()
        }

        if (playing) {
            timeline.pause();
            sceneViewerRef.current.killAllCurrentCameraTweens();
        } else {
            timeline.play();
        }
        setPlaying(!playing);
    }, [playing, timeline]);

    /**
     * Handles the slider change event
     * 
     * @param {Object} value The slider value
     */
    const handleSliderChange = useCallback((value) => {
        const newCursor = value[0];

        timeline.seek(newCursor / 100 * timeline.duration()).pause(); // Seek and pause to allow precise control
        setCursor(newCursor);
        setPlaying(false);
    }, [timeline]);

    /**
     * Handles the slider change end event
     */
    const handleSliderChangeEnd = useCallback(() => {
        if (playing) timeline.play();
    }, [playing, timeline]);

    const duration = useMemo(() => (parseFloat(crashLabels[crashLabels?.length - 1]) - parseFloat(crashLabels[0])), [crashLabels]);

    const played = useMemo(() => duration * cursor / 100, [duration, cursor]);

    useEffect(() => {
        // Sync cursor with timeline's progress
        const updateCursor = () => setCursor((timeline.progress() * 100)?.toFixed(2));
        timeline.eventCallback("onUpdate", updateCursor);

        const stopTimeline = () => {
            setPlaying(false);
        }
        const startTimeline = () => {
            setPlaying(true);
        }

        timeline.eventCallback("onComplete", stopTimeline);
        timeline.eventCallback("onStart", startTimeline);
        timeline.play()
    }, [timeline]);

    return (
        <PrimaryCard
            title={t("plugin.player.title")}
        >
            <VStack justifyContent={"flex-end"} width={"full"} height={"full"} zIndex={100} paddingBottom={"10px"}>
                <Box height={"150px"} width={"full"} id={"player"} position={"relative"}>
                    <VStack position={"absolute"} width={"full"} justifyContent={"space-between"} paddingY={"8px"}>
                        <HStack width={"full"} justifyContent={"flex-start"} paddingX={"8px"}>
                            <PrimaryTag width={"175px"} zIndex={100}>
                                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                                    {t("plugin.speedBeforeCrash") + (maxSpeed || 0)} km/h
                                </TagLabel>
                            </PrimaryTag>
                        </HStack>
                    </VStack>
                    <Box as={Canvas} borderRadius={"7px"} >
                        <SceneViewer timeline={timeline} ref={sceneViewerRef} />
                    </Box>
                </Box>
                <Box width={"full"} height={"100px"} backgroundColor={"#5B5F6199"} borderRadius={"7px"} position={"relative"}>
                    <VStack position={"absolute"} width={"full"} height={"full"} justifyContent={"flex-end"} paddingY={"8px"}>
                        <HStack width={"full"} justifyContent={"flex-end"} paddingX={"8px"}>
                            <PrimaryTag width={"70px"}>
                                <TagLabel fontSize={"12px"} fontWeight={300} textAlign={"start"}>
                                    {currentSpeed || 0} km/h
                                </TagLabel>
                            </PrimaryTag>
                        </HStack>
                    </VStack>
                    <Box
                        as={Line}
                        ref={chart}
                        data={data}
                        options={options}
                        marginLeft={"16px"}
                    />
                </Box>
                <HStack width={"full"} height={"20px"} gap={"12px"}>
                    <IconButton
                        size={"xs"}
                        color={"white"}
                        variant={"ghost"}
                        onClick={togglePlayPause}
                        icon={<Icon as={playing ? MdPause : MdPlayArrow} boxSize={"20px"} />}
                    />
                    <RangeSlider
                        size={"md"}
                        value={[cursor]}
                        min={0}
                        max={100}
                        onChange={handleSliderChange}
                        onChangeEnd={handleSliderChangeEnd}
                    >
                        <RangeSliderTrack bg={"gray.300"} height={"10px"} borderRadius={"full"} />
                        <RangeSliderThumb index={0} width={"15px"} height={"15px"}>
                            <Box position={"absolute"} bottom={0} height={"120px"} borderLeftWidth={"1px"} borderStyle={"solid"} borderColor={"white"} />
                        </RangeSliderThumb>
                    </RangeSlider>
                </HStack>
                <HStack width={"full"} height={"20px"} padding={"10px"} gap={"10px"} justifyContent={"space-between"}>
                    <Text>{Math.floor(played).toFixed(0).padStart(2, "0")}:{((played % 1) * 99).toFixed(0).padStart(2, "0")}</Text>
                    <Text>{Math.floor(duration).toFixed(0).padStart(2, "0")}:{((duration % 1) * 99).toFixed(0).padStart(2, "0")}</Text>
                </HStack>
            </VStack>
        </PrimaryCard >

    );
};

export default React.memo(PlayerCard);
